import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  MapPin, 
  User, 
  Home, 
  Calendar,
  Upload,
  FileText,
  ChefHat,
  Award,
  Droplets,
  Shield,
  AlertCircle,
  Bath,
  Trash2,
  Thermometer,
  Eye,
  ChevronLeft,
  Download,
  Check,
  X,
  Globe,
  Navigation
} from 'lucide-react';
import { useLanguage } from "../../context/LanguageContext";

const KitchenSurveyDetail = ({ 
  survey, 
  detail, 
  onBack,
  loading 
}) => {
  const { t } = useLanguage();
  const [locationName, setLocationName] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (detail?.info?.latitude && detail?.info?.longitude) {
      fetchLocationFromCoords(detail.info.latitude, detail.info.longitude);
    }
  }, [detail?.info?.latitude, detail?.info?.longitude]);

  const fetchLocationFromCoords = async (lat, lng) => {
    try {
      setMapLoading(true);
      const apiKey = process.env.REACT_APP_MAP_API_KEY;
      
      if (!apiKey) {
        setLocationName(`${t.global.location_label}: ${lat}, ${lng}`);
        return;
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
      );
      
      const data = await response.json();
      
      if (data.status === 'OK' && data.results.length > 0) {
        const address = data.results[0].formatted_address;
        setLocationName(address);
      } else {
        setLocationName(`${t.global.location_label}: ${lat}, ${lng}`);
      }
    } catch (error) {
      setLocationName(`${t.global.location_label}: ${lat}, ${lng}`);
    } finally {
      setMapLoading(false);
    }
  };

  if (!survey || !detail) return null;

  // Status badge component
  const StatusBadge = ({ value, showText = true }) => {
    if (value === "Yes" || value === "1" || value === 1 || value === true) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check size={12} className="mr-1" />
          {showText && t.global.yes}
        </span>
      );
    } else if (value === "No" || value === "0" || value === 0 || value === false) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <X size={12} className="mr-1" />
          {showText && t.global.no}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock size={12} className="mr-1" />
        {showText && t.global.pending}
      </span>
    );
  };

  // Rating badge for quality/quantity ratings
  const RatingBadge = ({ value }) => {
    const colors = {
      'Good': 'bg-green-100 text-green-800',
      'Satisfactory': 'bg-yellow-100 text-yellow-800',
      'Poor': 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors[value] || 'bg-gray-100 text-gray-800'}`}>
        {value}
      </span>
    );
  };

  const renderField = (label, value, icon = null) => {
    if (value === null || value === undefined || value === "") return null;
    
    const displayValue = () => {
      if (typeof value === 'boolean') return value ? t.global.yes : t.global.no;
      if (value === "1") return t.global.yes;
      if (value === "0") return t.global.no;
      if (value === 1) return t.global.yes;
      if (value === 0) return t.global.no;
      return value;
    };

    return (
      <div className="flex items-start mb-2">
        {icon && <div className="mr-2 mt-0.5">{icon}</div>}
        <div className="flex-1">
          <span className="font-medium text-gray-700">{label}: </span>
          <span className="text-gray-900">
            {displayValue()}
          </span>
        </div>
      </div>
    );
  };

  const renderUploadField = (label, url) => {
    if (!url) return null;
    
    const fullUrl = url.startsWith('http') ? url : `https://dkp.wmgt.in:8005/storage/${url}`;
    
    return (
      <div className="mb-2">
        <span className="font-medium text-gray-700">{label}: </span>
        <a 
          href={fullUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline flex items-center"
        >
          <Download size={14} className="mr-1" />
          {t.global.view_upload}
        </a>
      </div>
    );
  };

  const getSectionTitle = (sectionKey) => {
    const titles = {
      info: t.global.section_kitchen_info,
      gen_data: t.global.section_general_info,
      quali_quanti_asment: t.global.section_quality_quantity_assessment,
      water_supply: t.global.section_water_supply_quality,
      kitch_spce: t.global.section_kitchen_space,
      sfty_cleans: t.global.section_safety_arrangements,
      sanitation: t.global.section_sanitation_cleanliness,
      wst_mngemnt: t.global.section_waste_management,
      pers_hygn: t.global.section_personal_hygiene,
      food_hygn: t.global.section_food_hygiene,
      iec_mon_J1: t.global.section_iec_monitoring,
      others: t.global.section_other_details
    };
    
    return titles[sectionKey] || sectionKey.replace(/_/g, ' ').toUpperCase();
  };

  const getSectionIcon = (sectionKey) => {
    const icons = {
      info: <ChefHat size={18} />,
      gen_data: <FileText size={18} />,
      quali_quanti_asment: <Award size={18} />,
      water_supply: <Droplets size={18} />,
      kitch_spce: <Home size={18} />,
      sfty_cleans: <Shield size={18} />,
      sanitation: <Bath size={18} />,
      wst_mngemnt: <Trash2 size={18} />,
      pers_hygn: <Thermometer size={18} />,
      food_hygn: <AlertCircle size={18} />,
      iec_mon_J1: <Eye size={18} />,
      others: <FileText size={18} />
    };
    
    return icons[sectionKey] || <FileText size={18} />;
  };

  // Map API field names to human-readable labels
  const getFieldLabel = (fieldKey) => {
    const fieldMappings = {
      // General Information
      'gen_info_A1': t.global.q_kitchen_functional,
      'gen_info_A1_remark': t.global.q_remarks_non_functioning,
      'gen_info_A2': t.global.q_operated_by,
      'gen_info_A2_remark': t.global.q_operation_remarks,
      'gen_info_A3': t.global.q_cook_cum_helpers,
      'gen_info_A4': t.global.q_kitchen_timeline,
      'gen_info_A5': t.global.q_avg_beneficiaries,
      
      // Quality & Quantity Assessment
      'quali_quanti_asment_B1': t.global.q_raw_materials_quality,
      'quali_quanti_asment_B2': t.global.q_meal_quality,
      'quali_quanti_asment_B3': t.global.q_items_cooked_daily,
      
      // Water Supply
      'water_supply_C1': t.global.q_adequate_water_storage,
      'water_supply_C2': t.global.q_water_filter_installed,
      'water_supply_C3': t.global.q_tanks_cleaned,
      'water_supply_C3_date': t.global.q_last_tank_cleaning_date,
      'water_supply_C3_upload': t.global.q_tank_cleaning_picture,
      'water_supply_C4': t.global.q_water_tested,
      'water_supply_C4_date': t.global.q_last_water_testing_date,
      'water_supply_C4_upload': t.global.q_test_report,
      
      // Kitchen Space
      'kitch_spce_D1': t.global.q_kitchen_covered,
      'kitch_spce_D2': t.global.q_proper_slabs,
      'kitch_spce_D3': t.global.q_functional_sink,
      'kitch_spce_D3_upload': t.global.q_sink_picture,
      'kitch_spce_D4': t.global.q_well_ventilated,
      'kitch_spce_D5': t.global.q_deep_cleaning_schedule,
      'kitch_spce_D5_upload': t.global.q_housekeeping_record,
      'kitch_spce_D6': t.global.q_store_area_maintained,
      'kitch_spce_D6_upload': t.global.q_store_housekeeping_record,
      'kitch_spce_D7': t.global.q_lpg_used,
      'kitch_spce_D8': t.global.q_cleanliness_rating,
      
      // Safety Arrangements
      'sfty_cleans_E1': t.global.q_fire_extinguisher,
      'sfty_cleans_E1_date': t.global.q_last_refill_date,
      'sfty_cleans_E1_upload': t.global.q_extinguisher_picture,
      'sfty_cleans_E2': t.global.q_first_aid_box,
      'sfty_cleans_E3': t.global.q_fire_training,
      
      // Sanitation
      'sanitation_F1': t.global.q_staff_toilet,
      'sanitation_F2': t.global.q_toilet_ventilated,
      'sanitation_F3': t.global.q_toilets_cleaned_daily,
      'sanitation_F3_upload': t.global.q_toilet_housekeeping_record,
      'sanitation_F4': t.global.q_covered_dustbin,
      'sanitation_F4_upload': t.global.q_dustbin_picture,
      'sanitation_F5': t.global.q_covered_drainage,
      'sanitation_F6': t.global.q_cleaning_materials_not_in_toilet,
      'sanitation_F7': t.global.q_staff_cleans_area,
      'sanitation_F7_date': t.global.q_cleaning_verification_date,
      'sanitation_F7_upload': t.global.q_cleaning_system_picture,
      
      // Waste Management
      'wst_mngemnt_G1': t.global.q_waste_segregation,
      'wst_mngemnt_G2': t.global.q_iec_displayed,
      'wst_mngemnt_G2_upload': t.global.q_iec_picture,
      'wst_mngemnt_G3': t.global.q_bins_cleaned_daily,
      'wst_mngemnt_G3_upload': t.global.q_checklist_record,
      
      // Personal Hygiene
      'pers_hygn_H1': t.global.q_protective_gear,
      'pers_hygn_H1_upload': t.global.q_ppe_picture,
      'pers_hygn_H2': t.global.q_handwashing_practice,
      'pers_hygn_H3': t.global.q_health_checkups,
      'pers_hygn_H3_remark': t.global.q_health_checkup_remarks,
      'pers_hygn_H4': t.global.q_personal_hygiene,
      
      // Food Hygiene
      'food_hygn_I1': t.global.q_packaging_material,
      
      // IEC & Monitoring
      'iec_mon_J1': t.global.q_posters_displayed,
      
      // Others
      'challenges': t.global.q_challenges,
      'suggestions': t.global.q_suggestions
    };
    
    return fieldMappings[fieldKey] || fieldKey
      .replace(/_/g, ' ')
      .replace(/(?:^|\s)\S/g, a => a.toUpperCase());
  };

  const getFieldValue = (key, value) => {
    // Handle operation type (0=No, 1=Yes)
    if (key === 'gen_info_A1') {
      if (value === 0 || value === "0") return t.global.no;
      if (value === 1 || value === "1") return t.global.yes;
    }
    
    // Handle operation type (0=SHGs, 1=Others)
    if (key === 'gen_info_A2') {
      if (value === 0) return t.global.shgs;
      if (value === 1) return t.global.others;
    }
    
    // Handle packaging materials
    if (key === 'food_hygn_I1') {
      const packaging = {
        '0': t.global.packaging_aluminium,
        '1': t.global.packaging_plastic,
        '2': t.global.packaging_other
      };
      return packaging[value] || value;
    }
    
    // Handle time format
    if (key === 'gen_info_A4' && value && typeof value === 'string') {
      try {
        const timeParts = value.split(':');
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0]);
          const minutes = parseInt(timeParts[1]);
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        }
      } catch (e) {
        return value;
      }
    }
    
    // Handle quality ratings
    if (key.includes('B1') || key.includes('B2') || key.includes('D8')) {
      return value;
    }
    
    // Handle IEC monitoring
    if (key === 'iec_mon_J1') {
      if (value === 0 || value === "0") return t.global.no;
      if (value === 1 || value === "1") return t.global.yes;
    }
    
    // Handle Yes/No fields
    if (value === 0 || value === "0") return t.global.no;
    if (value === 1 || value === "1") return t.global.yes;
    
    return value;
  };

  const isBooleanField = (key, value) => {
    // Check if this is a boolean-like field based on value
    if (value === 0 || value === 1) return true;
    if (value === "0" || value === "1") return true;
    if (value === t.global.yes || value === t.global.no) return true;
    if (value === true || value === false) return true;
    
    // Check based on field name patterns
    const booleanPatterns = [
      /^gen_info_A[12]$/, /^quali_quanti_asment_B3$/,
      /^water_supply_C[1-4]$/, /^kitch_spce_D[1-7]$/, /^sfty_cleans_E[1-3]$/,
      /^sanitation_F[1-8]$/, /^wst_mngemnt_G[1-3]$/, /^pers_hygn_H[1-4]$/,
      /^iec_mon_J1$/
    ];
    
    return booleanPatterns.some(pattern => pattern.test(key));
  };

  const renderSection = (sectionKey, sectionData) => {
    if (!sectionData || Object.keys(sectionData).length === 0) return null;

    // Skip completion status fields
    const displayFields = Object.entries(sectionData).filter(([key]) => 
      !key.startsWith('is_') && !key.includes('_done')
    );

    if (displayFields.length === 0) return null;

    return (
      <div key={sectionKey} className="bg-white rounded-lg border border-gray-200 p-4 mb-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b">
          {getSectionIcon(sectionKey)}
          <h4 className="text-lg font-bold ml-2">
            {getSectionTitle(sectionKey)}
          </h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {displayFields.map(([key, value]) => {
            if (value === null || value === undefined || value === "") {
              return null;
            }
            
            const label = getFieldLabel(key);
            const displayValue = getFieldValue(key, value);
            const isRating = key.includes('B1') || key.includes('B2') || key.includes('D8');
            
            if (key.includes('upload')) {
              return renderUploadField(label, value);
            }
            
            return (
              <div key={key} className="p-2 bg-gray-50 rounded">
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-gray-600">{label}</span>
                  {isRating ? (
                    <RatingBadge value={displayValue} />
                  ) : isBooleanField(key, value) ? (
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      value === 1 || value === "1" || value === t.global.yes || value === true
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {value === 1 || value === "1" || value === t.global.yes || value === true ? (
                        <Check size={12} className="mr-1" />
                      ) : (
                        <X size={12} className="mr-1" />
                      )}
                      {displayValue}
                    </div>
                  ) : (
                    <span className="text-gray-900 font-medium">{displayValue}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLocationCard = () => {
    const lat = detail?.info?.latitude;
    const lng = detail?.info?.longitude;
    const fullAddress = detail?.info?.kitchen_location_fulladdress;
    const apiKey = process.env.REACT_APP_MAP_API_KEY;
    
    const coords = lat && lng ? `${lat}, ${lng}` : null;

    return (
      <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
        <div className="flex items-center mb-4">
          <Globe className="text-blue-600 mr-3" size={24} />
          <h3 className="text-xl font-bold text-gray-900">{t.global.location_details}</h3>
        </div>
        
        <div className="space-y-4">
          {/* Full Address if available */}
          {fullAddress && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="text-sm font-medium text-blue-700 mb-2">{t.global.full_address}</div>
              <div className="text-gray-800 leading-relaxed">
                {fullAddress}
              </div>
            </div>
          )}
          
          {/* Coordinates Display */}
          {coords && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <MapPin className="text-blue-700 mr-2" size={18} />
                  <div className="text-sm font-medium text-blue-700">{t.global.coordinates}</div>
                </div>
                <div className="text-xs text-blue-600 bg-white px-2 py-1 rounded">
                  {t.global.lat_long}
                </div>
              </div>
              <div className="font-mono text-sm bg-white p-3 rounded border border-blue-200 text-gray-800">
                {coords}
              </div>
              {mapLoading ? (
                <div className="mt-3 text-sm text-gray-600 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  {t.global.fetching_location}
                </div>
              ) : locationName && (
                <div className="mt-3 text-sm text-gray-700 bg-white p-3 rounded border border-blue-100">
                  📍 {locationName}
                </div>
              )}
            </div>
          )}

          {/* Interactive Map Section */}
          {apiKey && coords && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <Navigation className="text-gray-700 mr-2" size={18} />
                  <div className="text-sm font-medium text-gray-700">{t.global.interactive_map}</div>
                </div>
                <button
                  onClick={() => setShowMap(!showMap)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
                >
                  {showMap ? t.global.hide_map : t.global.show_map}
                </button>
              </div>
              
              {showMap && (
                <div className="mt-3 border border-gray-300 rounded-lg overflow-hidden">
                  <iframe
                    title="Kitchen Location Map"
                    width="100%"
                    height="250"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${coords}&zoom=15&maptype=roadmap`}
                    allowFullScreen
                  />
                </div>
              )}
              
              {/* Open in Google Maps link */}
              <div className="mt-3">
                <a
                  href={`https://www.google.com/maps?q=${coords}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  <Navigation size={14} className="mr-2" />
                  {t.global.open_in_google_maps}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getSectionCompletion = (sectionKey, sectionData) => {
    const completionKeys = {
      gen_data: 'is_gen_info_survey_done',
      quali_quanti_asment: 'is_quali_quanti_asment_survey_done',
      water_supply: 'is_water_supply_survey_done',
      kitch_spce: 'is_kitch_spce_survey_done',
      sfty_cleans: 'is_sfty_cleanl_survey_done',
      sanitation: 'is_sanitation_survey_done',
      wst_mngemnt: 'is_wst_mngemnt_survey_done',
      pers_hygn: 'is_pers_hygn_survey_done',
      food_hygn: 'is_food_hygn_survey_done',
      iec_mon_J1: 'is_iec_mon_survey_done'
    };
    
    const completionKey = completionKeys[sectionKey];
    if (!completionKey || !sectionData) return null;
    
    const completionValue = sectionData[completionKey];
    if (completionValue === "1" || completionValue === 1 || completionValue === t.global.yes || completionValue === true) {
      return true;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-4">
          <div className="flex items-center mb-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg mr-3"
            >
              <ChevronLeft size={24} />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{t.global.kitchen_survey_title}</h1>
              <p className="text-sm text-gray-500">{t.global.survey_id_label} #{survey.id}</p>
            </div>
            <div className={`px-4 py-2 rounded-full font-bold ${
              detail.info?.is_total_survey_complete === 1 
                ? 'bg-green-100 text-green-700' 
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {detail.info?.is_total_survey_complete === 1 ? t.global.completed : t.global.in_progress}
            </div>
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <Calendar size={16} className="mr-2" />
            <span>
              {new Date(survey.created_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
              {' • '}
              {new Date(survey.created_at).toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
            <p className="text-gray-600 mt-4 font-medium">{t.global.loading_survey_details}</p>
          </div>
        ) : (
          <>
            {/* Basic Info Card */}
            <div className="bg-white rounded-xl p-5 mb-4 border border-gray-200 shadow-sm">
              <div className="flex items-center mb-5">
                <ChefHat className="text-blue-600 mr-3" size={24} />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{t.global.kitchen_details}</h2>
                  <p className="text-sm text-gray-500">{t.global.maa_programme_inspection}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <Home size={16} className="mr-2" />
                    {t.global.kitchen_name}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {detail.info?.area_name || t.global.na}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <FileText size={16} className="mr-2" />
                    {t.global.kitchen_id}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {detail.info?.id || t.global.na}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <ChefHat size={16} className="mr-2" />
                    {t.global.type_label}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {detail.info?.canteen_type_name || t.global.na}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                  <div className="text-sm text-gray-500 mb-1">{t.global.district}</div>
                  <div className="text-gray-900 font-medium">
                    {detail.info?.district_name || t.global.na}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                  <div className="text-sm text-gray-500 mb-1">{t.global.municipality_ulb}</div>
                  <div className="text-gray-900 font-medium">
                    {detail.info?.municipality_name || t.global.na}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                  <div className="text-sm text-gray-500 mb-1">{t.global.ward_no}</div>
                  <div className="text-gray-900 font-medium">
                    {detail.info?.ward_name || t.global.na}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 mb-2 border border-gray-200">
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <User size={16} className="mr-2" />
                    {t.global.surveyor}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {detail.info?.fullName || t.global.na}
                  </div>
                </div>
                
                <div className="col-span-1 md:col-span-2">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                    <div className="text-sm font-medium text-blue-700 mb-1">{t.global.reporting_date}</div>
                    <div className="text-blue-900 font-medium">
                      {detail.info?.reporting_date || t.global.not_specified}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Card */}
            {renderLocationCard()}

            {/* Survey Sections */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 px-2">{t.global.all_inspection_sections}</h2>
              <div className="space-y-4">
                {['gen_data', 'quali_quanti_asment', 'water_supply', 'kitch_spce', 
                  'sfty_cleans', 'sanitation', 'wst_mngemnt', 'pers_hygn', 
                  'food_hygn', 'iec_mon_J1', 'others']
                  .map(sectionKey => {
                    if (detail[sectionKey]) {
                      return renderSection(sectionKey, detail[sectionKey]);
                    }
                    return null;
                  })}
              </div>
            </div>

            {/* Completion Stats */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6 shadow-sm">
              <h4 className="text-lg font-bold mb-4 text-gray-800">{t.global.survey_completion_status}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(detail).map(([sectionKey, sectionData]) => {
                  if (sectionKey !== 'info') {
                    const isCompleted = getSectionCompletion(sectionKey, sectionData);
                    
                    if (isCompleted !== null) {
                      const sectionShortName = getSectionTitle(sectionKey).split(' ')[0];
                      
                      return (
                        <div key={sectionKey} className="text-center p-3 border rounded-lg">
                          <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-2 ${
                            isCompleted
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle size={20} />
                            ) : (
                              <Clock size={20} />
                            )}
                          </div>
                          <div className="text-sm font-medium">
                            {sectionShortName}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {isCompleted ? t.global.completed_short : t.global.pending_short}
                          </div>
                        </div>
                      );
                    }
                  }
                  return null;
                })}
              </div>
            </div>

            {/* Overall Status */}
            {detail.info?.is_total_survey_complete === 1 && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 mb-4">
                <div className="flex items-center">
                  <CheckCircle className="text-green-600 mr-3" size={24} />
                  <div>
                    <h3 className="text-lg font-bold text-green-800">{t.global.survey_completed_submitted}</h3>
                    <p className="text-green-600">{t.global.all_sections_submitted}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <button
          onClick={onBack}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold text-lg flex items-center justify-center transition duration-200"
        >
          <ChevronLeft className="mr-2" size={20} />
          {t.global.back_to_survey_list}
        </button>
      </div>
    </div>
  );
};

export default KitchenSurveyDetail;